import React from 'react';
import {Link} from 'react-router-dom';
import styled, {css} from 'styled-components';
import {Detail, Icon, Pill, PillVariant, Text} from '@forecast-it/design-system';
import {FeatureFlagRowData} from './featureFlagRows';
import {getTeam, UNMAPPED_TEAM_COLOUR} from './featureFlagTeams';
import {
	FEATURE_FLAG_CREATED_COLUMN_INSET,
	FEATURE_FLAG_GRID_COLUMNS,
	FEATURE_FLAG_GRID_GAP,
	FEATURE_FLAG_GRID_PADDING_LEFT,
	FEATURE_FLAG_GRID_PADDING_RIGHT,
} from './featureFlagGrid';
import {FeatureFlagRolloutBar} from './FeatureFlagRolloutBar';
import {
	FEATURE_FLAG_CARD_BORDER,
	FEATURE_FLAG_CARD_HOVER_BACKGROUND,
	FEATURE_FLAG_CARD_HOVER_BORDER,
	FEATURE_FLAG_CARD_SURFACE_BACKGROUND,
	featureFlagCardSurface,
} from './featureFlagSurface';

// A row is either a link to its flag's detail page or inert, and the two have to look identical, so
// both draw from this one block rather than each keeping their own copy of the grid.
const rowCard = css<{$interactive: boolean}>`
	${featureFlagCardSurface}
	position: relative;
	display: grid;
	grid-template-columns: ${FEATURE_FLAG_GRID_COLUMNS};
	align-items: center;
	gap: ${FEATURE_FLAG_GRID_GAP};
	min-height: 56px;
	padding: 8px ${FEATURE_FLAG_GRID_PADDING_RIGHT} 8px ${FEATURE_FLAG_GRID_PADDING_LEFT};
	overflow: hidden;
	cursor: ${props => (props.$interactive ? 'pointer' : 'default')};

	&:hover {
		border-color: ${props => (props.$interactive ? FEATURE_FLAG_CARD_HOVER_BORDER : FEATURE_FLAG_CARD_BORDER)};
		background: ${props =>
			props.$interactive ? FEATURE_FLAG_CARD_HOVER_BACKGROUND : FEATURE_FLAG_CARD_SURFACE_BACKGROUND};
	}
`;

const RowCard = styled.div<{$interactive: boolean}>`
	${rowCard}
`;

/**
 * The row as a real link, so it can be tabbed to, opened in a new tab, and its URL copied — a click
 * handler on a div gives a keyboard-only admin no way in at all. Stripped back to reading as a row
 * rather than as link text.
 */
const RowCardLink = styled(Link)<{$interactive: boolean}>`
	${rowCard}
	color: inherit;
	text-decoration: none;
`;

const AccentBar = styled.span<{$colour: string}>`
	position: absolute;
	left: 0;
	top: 0;
	bottom: 0;
	width: 4px;
	background: ${props => props.$colour};
`;

const FlagName = styled.span`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const TeamCell = styled.span`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const TeamSwatch = styled.span<{$colour: string}>`
	width: 10px;
	height: 10px;
	border-radius: 2px;
	flex: none;
	background: ${props => props.$colour};
`;

const NumberCell = styled.span`
	text-align: right;
	font-variant-numeric: tabular-nums;
`;

// Date over a quieter age line. The age is what makes a stale flag jump out; the date is there to
// answer "since when" without arithmetic. Both stay on one line each so a narrow column cannot wrap
// a row to double height and break the grid's alignment with the header.
const CreatedCell = styled.span`
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 0;
	padding-left: ${FEATURE_FLAG_CREATED_COLUMN_INSET};
`;

const CreatedLine = styled.span`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const RolloutCell = styled.span`
	display: flex;
	flex-direction: column;
	gap: 3px;
	align-items: flex-start;
`;

const ProgressRow = styled.span`
	display: flex;
	align-items: center;
	gap: 10px;
	width: 100%;
`;

const PercentLabel = styled.span`
	flex: none;
	width: 38px;
	text-align: right;
	font-variant-numeric: tabular-nums;
`;

const CleanedUpLine = styled.span`
	display: flex;
	align-items: center;
	gap: 6px;
	padding-left: 8px;
`;

const ChevronCell = styled.span`
	display: flex;
	justify-content: flex-end;
`;

export interface FeatureFlagRowProps {
	row: FeatureFlagRowData;
	/**
	 * Where the flag's detail page lives. A rolled-out flag is configured in code, so its row is inert
	 * unless a platform-wide grant is still recorded for it: that grant is removed from the detail page.
	 * A row with no path given stays inert too.
	 */
	detailPath?: string;
}

export function FeatureFlagRow({row, detailPath}: FeatureFlagRowProps): JSX.Element {
	// A team the map does not know yet still gets a row — drawn in neutral grey, under whatever name
	// the backend gave it (see teamLabel in featureFlagRows).
	const teamColour = row.teamId ? getTeam(row.teamId).colour : UNMAPPED_TEAM_COLOUR;
	const interactive = !row.isRolledOut || row.hasPlatformWideGrant;

	const cells = (
		<>
			{/* Purely decorative: it restates the team named in the cell after next, so giving it an
			    accessible name would make every row announce its colour before its team. */}
			<AccentBar aria-hidden="true" data-testid="feature-flag-accent-bar" $colour={teamColour} />
			<FlagName>
				<Text>{row.name}</Text>
			</FlagName>
			<TeamCell>
				<TeamSwatch $colour={teamColour} />
				<Text>{row.teamLabel}</Text>
			</TeamCell>
			<NumberCell>
				<Text>{row.companyCount}</Text>
			</NumberCell>
			<NumberCell>
				<Text>{row.userCount}</Text>
			</NumberCell>
			<CreatedCell>
				<CreatedLine>
					<Text>{row.createdDateLabel}</Text>
				</CreatedLine>
				<CreatedLine>
					<Detail color="medium">{row.createdAgeLabel}</Detail>
				</CreatedLine>
			</CreatedCell>
			<RolloutCell>
				{row.isRolledOut ? (
					<>
						<Pill variant={PillVariant.SUCCESS} icon="tick">
							{row.rolledOutDate ? `Rolled out ${row.rolledOutDate}` : 'Rolled out'}
						</Pill>
						<CleanedUpLine>
							<Icon size="s" icon={row.cleanedUp ? 'tick' : 'close'} />
							<Detail>{row.cleanedUp ? 'Cleaned up' : 'Not cleaned up'}</Detail>
						</CleanedUpLine>
					</>
				) : (
					<ProgressRow>
						<FeatureFlagRolloutBar percent={row.rolloutPercent} label={row.rolloutPercentLabel} height="6px" />
						<PercentLabel>
							<Detail>{row.rolloutPercentLabel}</Detail>
						</PercentLabel>
					</ProgressRow>
				)}
			</RolloutCell>
			<ChevronCell>{interactive ? <Icon size="m" icon="chevronRight" /> : null}</ChevronCell>
		</>
	);

	return interactive && detailPath != null ? (
		<RowCardLink $interactive={interactive} data-cy="feature-flag-row" to={detailPath}>
			{cells}
		</RowCardLink>
	) : (
		<RowCard $interactive={interactive} data-cy="feature-flag-row">
			{cells}
		</RowCard>
	);
}
